import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  getFundEliteBrandAssets,
  FUNDELITE_BRAND,
  readDocumentBrandTheme,
} from '../../utils/fundeliteBrandAssets';
import { shouldRenderFundEliteBrandMark } from '../../utils/fundeliteBrandingSurface';

/**
 * Portal-aware FundElite brand mark for existing sidebar / auth logo slots.
 * Never renders inside FundGenius HQ product surfaces.
 *
 * Self Repair wordmark follows resolved theme:
 * - light → logo for light backgrounds
 * - dark → logo for dark backgrounds
 * Compact / icon mode uses the green (or gold agency) favicon.
 *
 * @param {'self-repair' | 'agency-admin'} variant
 * @param {'auto' | 'logo' | 'icon'} mode
 * @param {'light' | 'dark'} [theme] - optional explicit resolved theme
 */
export default function FundEliteBrandMark({
  variant = 'self-repair',
  mode = 'auto',
  compact = false,
  theme,
  className = '',
  logoClassName = '',
  iconClassName = '',
  alt = FUNDELITE_BRAND.name,
  showWordmarkFallback = false,
}) {
  const location = useLocation();
  if (!shouldRenderFundEliteBrandMark({ pathname: location?.pathname })) {
    return null;
  }

  const resolvedTheme = theme === 'light' || theme === 'dark'
    ? theme
    : readDocumentBrandTheme();
  const assets = getFundEliteBrandAssets(variant, { theme: resolvedTheme });
  const useIcon = mode === 'icon' || (mode === 'auto' && compact);
  const src = useIcon ? assets.icon : assets.logo;

  if (useIcon) {
    return (
      <img
        src={src}
        alt={alt}
        className={iconClassName || `h-10 w-10 object-contain ${className}`.trim()}
        draggable={false}
        data-testid="fundelite-brand-mark"
        data-brand-variant={variant}
        data-brand-mode="icon"
        data-brand-theme={resolvedTheme}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={
        logoClassName ||
        `h-8 w-auto max-w-[148px] object-contain object-left ${className}`.trim()
      }
      draggable={false}
      data-testid="fundelite-brand-mark"
      data-brand-variant={variant}
      data-brand-mode="logo"
      data-brand-theme={resolvedTheme}
    />
  );
}
